/**
 * AI analysis add-ons: catalog, data summaries, request preview and a provider-agnostic run gate.
 * No provider is registered yet, so runAnalysis always stops with AI_NOT_CONNECTED.
 * Pure module, mirrored in apps/mobile/src/services/aiAnalysis.ts; keep both identical.
 */

export const AI_CONFIG_DOC = ["config", "aiAnalysis"];

/** Record fields used to summarize each dataset. Only aggregates leave the device, never rows. */
export const DATASETS = {
  invoices: { date: "createdAt", status: "status", amounts: ["totalAmount", "taxAmount"], group: "type" },
  accounts: { date: "createdAt", status: "type", amounts: ["balance"], group: "currency" },
  approvals: { date: "createdAt", status: "status", amounts: ["amount"], group: "type" },
  products: { date: "createdAt", status: "status", amounts: ["currentStock", "costPrice"], group: "category" },
  stockMovements: { date: "createdAt", status: "type", amounts: ["quantity"], group: "reason" },
  warehouses: { date: "createdAt", status: "status", amounts: [], group: "country" },
  suppliers: { date: "createdAt", status: "status", amounts: [], group: "country" },
  purchaseOrders: { date: "createdAt", status: "status", amounts: ["totalAmount"], group: "currency" },
  rfqs: { date: "createdAt", status: "status", amounts: [], group: null },
  customers: { date: "createdAt", status: "status", amounts: [], group: "country" },
  salesOrders: { date: "createdAt", status: "status", amounts: ["totalAmount"], group: "currency" },
  productionPlans: { date: "createdAt", status: "status", amounts: ["plannedQty"], group: null },
  manufacturingOrders: { date: "createdAt", status: "status", amounts: ["plannedQty", "completedQty", "scrappedQty"], group: null },
  inspections: { date: "createdAt", status: "status", amounts: [], group: "result" },
  nonConformances: { date: "createdAt", status: "status", amounts: [], group: "severity" },
  exportOrders: { date: "createdAt", status: "status", amounts: ["totalAmount"], group: "destinationCountry" },
  internationalShipments: { date: "createdAt", status: "status", amounts: [], group: "mode" },
  customsDeclarations: { date: "createdAt", status: "status", amounts: ["dutiesAmount", "taxAmount"], group: "currency" },
  returns: { date: "createdAt", status: "status", amounts: [], group: "reason" },
  rmas: { date: "createdAt", status: "status", amounts: [], group: null },
  warranties: { date: "createdAt", status: "status", amounts: [], group: null },
  serviceOrders: { date: "createdAt", status: "status", amounts: [], group: null },
  files: { date: "createdAt", status: "status", amounts: ["size"], group: "mimeType" },
  supportTickets: { date: "createdAt", status: "status", amounts: [], group: "priority" }
};

/** Analyses per module. icon names are Ionicons (mobile); web maps them to its own icon set. */
export const AI_MODULES = {
  dashboard: {
    icon: "speedometer-outline",
    analyses: [
      { id: "executiveSummary", datasets: ["invoices", "salesOrders", "purchaseOrders", "products"] },
      { id: "riskRadar", datasets: ["invoices", "approvals", "nonConformances"] }
    ]
  },
  files: {
    icon: "folder-open-outline",
    analyses: [
      { id: "documentInsights", datasets: ["files"] },
      { id: "duplicateDocuments", datasets: ["files"] }
    ]
  },
  accounting: {
    icon: "cash-outline",
    analyses: [
      { id: "cashFlowForecast", datasets: ["invoices"] },
      { id: "receivableRisk", datasets: ["invoices", "accounts"] },
      { id: "invoiceAnomaly", datasets: ["invoices"] },
      { id: "approvalBottleneck", datasets: ["approvals"] }
    ]
  },
  inventory: {
    icon: "cube-outline",
    analyses: [
      { id: "demandForecast", datasets: ["stockMovements", "products"] },
      { id: "deadStock", datasets: ["products", "stockMovements"] },
      { id: "reorderOptimization", datasets: ["products", "stockMovements"] },
      { id: "warehouseUtilization", datasets: ["warehouses", "stockMovements"] }
    ]
  },
  procurement: {
    icon: "cart-outline",
    analyses: [
      { id: "supplierRisk", datasets: ["suppliers", "purchaseOrders"] },
      { id: "priceAnomaly", datasets: ["purchaseOrders", "rfqs"] },
      { id: "spendAnalysis", datasets: ["purchaseOrders", "suppliers"] }
    ]
  },
  sales: {
    icon: "storefront-outline",
    analyses: [
      { id: "salesForecast", datasets: ["salesOrders"] },
      { id: "customerChurn", datasets: ["customers", "salesOrders"] },
      { id: "pricingInsight", datasets: ["salesOrders"] }
    ]
  },
  manufacturing: {
    icon: "construct-outline",
    analyses: [
      { id: "capacityBottleneck", datasets: ["productionPlans", "manufacturingOrders"] },
      { id: "scrapAnalysis", datasets: ["manufacturingOrders"] }
    ]
  },
  quality: {
    icon: "shield-checkmark-outline",
    analyses: [
      { id: "defectRootCause", datasets: ["nonConformances", "inspections"] },
      { id: "qualityTrend", datasets: ["inspections"] }
    ]
  },
  export: {
    icon: "globe-outline",
    analyses: [
      { id: "shipmentDelay", datasets: ["internationalShipments"] },
      { id: "customsCost", datasets: ["customsDeclarations", "exportOrders"] }
    ]
  },
  afterSales: {
    icon: "build-outline",
    analyses: [
      { id: "failurePatterns", datasets: ["rmas", "returns"] },
      { id: "warrantyCost", datasets: ["warranties", "serviceOrders"] }
    ]
  },
  enterprise: {
    icon: "business-outline",
    analyses: [{ id: "processInsights", datasets: ["approvals"] }]
  },
  support: {
    icon: "help-buoy-outline",
    analyses: [{ id: "ticketTriage", datasets: ["supportTickets"] }]
  }
};

export const MODULE_IDS = Object.keys(AI_MODULES);

export function analysesFor(moduleId) {
  return (AI_MODULES[moduleId] || AI_MODULES.dashboard).analyses;
}

export function datasetsFor(moduleId) {
  const out = [];
  for (const a of analysesFor(moduleId)) for (const d of a.datasets) if (!out.includes(d)) out.push(d);
  return out;
}

const PATH_MODULES = [
  ["/accounting", "accounting"], ["/inventory", "inventory"], ["/procurement", "procurement"],
  ["/manufacturing", "manufacturing"], ["/quality", "quality"], ["/packaging", "quality"],
  ["/export", "export"], ["/sales", "sales"], ["/after-sales", "afterSales"], ["/enterprise", "enterprise"],
  ["/files", "files"], ["/file", "files"], ["/support", "support"]
];

/** Web path (with or without /admin) → module id; unknown pages get the dashboard analyses. */
export function moduleForPath(path) {
  const p = String(path || "").replace(/^\/admin(?=\/|$)/, "") || "/";
  const hit = PATH_MODULES.find(([prefix]) => p === prefix || p.startsWith(`${prefix}/`));
  return hit ? hit[1] : "dashboard";
}

const ROUTE_MODULES = [
  [/^(Invoice|Account|Transaction|Approval|Accounting|Budget|Expense|Payment|Finance|Ledger)/, "accounting"],
  [/^(Product|Stock|Warehouse|Lot|Serial|Inventory|Shipment|Purchase(Request)|Replenish|Pick|Count|Transfer|Location|GoodsReceipt)/, "inventory"],
  [/^(Supplier|Rfq|RFQ|PurchaseOrder|Procurement)/, "procurement"],
  [/^(Customer|PriceList|SalesOrder|Sales|Delivery)/, "sales"],
  [/^(Production|Manufacturing|WorkOrder|Bom|BOM|Mrp|MRP)/, "manufacturing"],
  [/^(Quality|Inspection|NonConformance|Capa|CAPA|Packag|Pallet)/, "quality"],
  [/^(Export|IntlShipment|International|Container|Forwarder|Customs|Overseas)/, "export"],
  [/^(Return|Rma|RMA|Warranty|ServiceOrder|AfterSales)/, "afterSales"],
  [/^(Enterprise|MasterData|Notification|ApprovalInbox)/, "enterprise"],
  [/^(File|Folder|Trash|OCR)/, "files"],
  [/^(Support|NewSupportTicket)/, "support"]
];

/** Mobile route name → module id. Order matters: PurchaseOrder before Purchase*. */
export function moduleForRoute(routeName) {
  const name = String(routeName || "");
  if (/^PurchaseOrder/.test(name)) return "procurement";
  if (/^ApprovalInbox/.test(name)) return "enterprise";
  const hit = ROUTE_MODULES.find(([rx]) => rx.test(name));
  return hit ? hit[1] : "dashboard";
}

function toMs(v) {
  if (!v) return null;
  if (typeof v === "number") return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
}

function isoDay(ms) {
  return ms === null ? null : new Date(ms).toISOString().slice(0, 10);
}

function topCounts(map, n) {
  return Object.entries(map).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, n);
}

/**
 * Aggregate view of a dataset: counts, date range, recent activity, amount totals, status and group breakdowns.
 * Free text (names, notes, emails) is never copied.
 */
export function summarizeDataset(rows, spec = {}, { now = Date.now() } = {}) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && !r.deletedAt) : [];
  const dates = [];
  const sums = {};
  const byStatus = {};
  const byGroup = {};
  let last30 = 0;
  for (const r of list) {
    const ms = toMs(spec.date ? r[spec.date] : r.createdAt);
    if (ms !== null) {
      dates.push(ms);
      if (now - ms <= 30 * 86400000 && ms <= now) last30++;
    }
    for (const f of spec.amounts || []) {
      const n = Number(r[f]);
      if (Number.isFinite(n)) sums[f] = Math.round(((sums[f] || 0) + n) * 100) / 100;
    }
    if (spec.status) {
      const s = r[spec.status];
      if (s !== undefined && s !== null && s !== "") byStatus[String(s)] = (byStatus[String(s)] || 0) + 1;
    }
    if (spec.group) {
      const g = r[spec.group];
      if (g !== undefined && g !== null && g !== "") {
        const key = maskPII(String(g)).slice(0, 40);
        byGroup[key] = (byGroup[key] || 0) + 1;
      }
    }
  }
  return {
    count: list.length,
    from: dates.length ? isoDay(Math.min(...dates)) : null,
    to: dates.length ? isoDay(Math.max(...dates)) : null,
    last30,
    sums,
    byStatus: topCounts(byStatus, 6),
    byGroup: topCounts(byGroup, 5)
  };
}

function asRows(result) {
  if (Array.isArray(result)) return result;
  if (result && typeof result === "object") {
    for (const k of ["items", "rows", "data", "docs"]) if (Array.isArray(result[k])) return result[k];
  }
  return [];
}

/**
 * Runs the platform loaders ({ datasetId: () => Promise<rows> }) and summarises each dataset.
 * A failing loader only drops its own dataset.
 */
export async function loadSummaries(datasetIds, loaders, { now = Date.now() } = {}) {
  const results = await Promise.all(
    (datasetIds || []).map(async (id) => {
      const load = loaders && loaders[id];
      if (!load || !DATASETS[id]) return [id, null];
      try {
        return [id, summarizeDataset(asRows(await load()), DATASETS[id], { now })];
      } catch {
        return [id, null];
      }
    })
  );
  return {
    summaries: Object.fromEntries(results.filter(([, s]) => s)),
    failed: results.filter(([, s]) => !s).map(([id]) => id)
  };
}

/** Resolves with the first snapshot of a realtime subscription, then unsubscribes. */
export function firstSnapshot(subscribe) {
  return new Promise((resolve, reject) => {
    let done = false;
    let unsub = null;
    const finish = (fn, v) => {
      if (done) return;
      done = true;
      fn(v);
      if (unsub) unsub();
    };
    unsub = subscribe((rows) => finish(resolve, rows), (err) => finish(reject, err));
    if (done && unsub) unsub();
  });
}

/** Normalises config/aiAnalysis; anything but an explicit `enabled: true` means off. */
export function normalizeAiConfig(data) {
  return {
    enabled: !!data && data.enabled === true,
    provider: data && typeof data.provider === "string" && data.provider ? data.provider : null
  };
}

const PII_PATTERNS = [
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]"],
  [/\b[A-Z]{2}\d{2}(?: ?[A-Z0-9]{4}){2,7}(?: ?[A-Z0-9]{1,4})?\b/g, "[iban]"],
  [/(?:\+|00)\d[\d\s()./-]{7,}\d/g, "[phone]"],
  [/\b\d{10,11}\b/g, "[id]"]
];

/** Masks emails, IBANs, phone numbers and 10–11 digit ids (VKN / TCKN). */
export function maskPII(value) {
  let s = String(value ?? "");
  for (const [rx, label] of PII_PATTERNS) s = s.replace(rx, label);
  return s;
}

function deepMask(v) {
  if (typeof v === "string") return maskPII(v);
  if (Array.isArray(v)) return v.map(deepMask);
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, deepMask(x)]));
  return v;
}

/** The exact payload a provider would receive; shown to the user as a preview before anything is sent. */
export function buildAnalysisRequest({ moduleId, analysisId, summaries, language = "tr", now = Date.now() }) {
  const analysis = analysesFor(moduleId).find((a) => a.id === analysisId);
  const data = {};
  for (const d of analysis ? analysis.datasets : []) if (summaries && summaries[d]) data[d] = summaries[d];
  return deepMask({
    version: 1,
    module: moduleId,
    analysis: analysisId,
    language,
    generatedAt: new Date(now).toISOString(),
    data
  });
}

// ---------------------------------------------------------------------------
// Provider gate

const providers = {};

/** A provider implements { id, analyze(request) => Promise<{ summary, findings: [{title, detail, severity}] }> }. */
export function registerAiProvider(provider) {
  if (!provider || !provider.id || typeof provider.analyze !== "function") throw new Error("INVALID_AI_PROVIDER");
  providers[provider.id] = provider;
}

export function unregisterAiProvider(id) {
  delete providers[id];
}

export function getAiProvider(id) {
  return (id && providers[id]) || null;
}

/**
 * 'disabled'    — admin switch (config/aiAnalysis.enabled) is off
 * 'no_provider' — switch on but no provider registered for config.provider
 * 'no_consent'  — user has not allowed AI data processing
 * 'ready'
 */
export function aiStatus({ config, consent } = {}) {
  if (!config || config.enabled !== true) return "disabled";
  if (!getAiProvider(config.provider)) return "no_provider";
  if (!consent) return "no_consent";
  return "ready";
}

export async function runAnalysis(request, { config, consent } = {}) {
  const status = aiStatus({ config, consent });
  if (status === "disabled") throw Object.assign(new Error("AI_DISABLED"), { code: "AI_DISABLED" });
  if (status === "no_provider") throw Object.assign(new Error("AI_NOT_CONNECTED"), { code: "AI_NOT_CONNECTED" });
  if (status === "no_consent") throw Object.assign(new Error("AI_NO_CONSENT"), { code: "AI_NO_CONSENT" });
  return getAiProvider(config.provider).analyze(request);
}
